import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const STEPS = [
  { key: 'upload',    label: 'Upload spreadsheet' },
  { key: 'validate',  label: 'Validate' },
  { key: 'review',    label: 'Review' },
  { key: 'submit',    label: 'Submit to Telus' },
];

const STATUS_META = {
  'dry-run-ok':                { label: 'Dry-run OK',           color: 'success' },
  'submitted':                 { label: 'Submitted',            color: 'success' },
  'submitted-name-mismatch':   { label: 'Submitted (name flag)', color: 'warning' },
  'submitted-amount-mismatch': { label: 'Amount mismatch',      color: 'error'   },
  'dry-run-ok-name-mismatch':  { label: 'Dry-run OK (name flag)', color: 'warning' },
  'submit-unverified':         { label: 'Needs check',          color: 'warning' },
  'name-mismatch':             { label: 'Name mismatch',        color: 'warning' },
  'claim-mismatch':            { label: 'Claim mismatch',       color: 'warning' },
  'claim-not-found':           { label: 'Claim not found',      color: 'warning' },
  'validation-failed':         { label: 'Validation failed',    color: 'error'   },
  'error':                     { label: 'Error',                color: 'error'   },
};

/**
 * WSIB workflow landing page for the accounting role.
 *
 * Full flow: upload → validate → review → choose login + credentials + dry-run
 * → live-submit confirmation (if live) → SSE progress → final results +
 * downloadable CSV.
 *
 * @returns {JSX.Element}
 */
export default function AccountingDashboard() {
  const { user, logout, token } = useAuth();

  // Upload + validation
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [result, setResult] = useState(null);

  // Submit
  const [credsOpen, setCredsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCreds, setPendingCreds] = useState(null);
  const [job, setJob] = useState(null);          // { id, dryRun, totalInvoices }
  const [jobEvents, setJobEvents] = useState([]);
  const [jobStatus, setJobStatus] = useState(null); // 'running' | 'done' | 'failed' | 'cancelled'
  const [jobFinal, setJobFinal] = useState(null);
  const [streamLost, setStreamLost] = useState(false);

  const [pastJobs, setPastJobs] = useState([]);
  const [pastJobsLoading, setPastJobsLoading] = useState(false);
  const [pastJobsError, setPastJobsError] = useState(null);

  const loadPastJobs = useCallback(async () => {
    if (!token) return;
    setPastJobsLoading(true);
    setPastJobsError(null);
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/accounting/wsib/jobs`,
        { headers: { Authorization: `Bearer ${String(token).trim()}` } }
      );
      setPastJobs(data?.jobs ?? []);
    } catch (e) {
      setPastJobsError(e?.response?.data?.error || e.message || 'Failed to load past runs');
    } finally {
      setPastJobsLoading(false);
    }
  }, [token]);

  useEffect(() => { void loadPastJobs(); }, [loadPastJobs]);

  // Refresh past runs when the current job completes.
  useEffect(() => {
    if (jobStatus === 'done' || jobStatus === 'failed' || jobStatus === 'cancelled') {
      void loadPastJobs();
    }
  }, [jobStatus, loadPastJobs]);

  /**
   * Switches the dashboard's current job context to a past run. Running jobs
   * resume their live feed via the SSE useEffect; terminal jobs render from
   * the /result snapshot.
   */
  const handleWatchJob = useCallback(async (pastJob) => {
    setFile(null);
    setResult(null);
    setUploadError(null);
    setJobEvents([]);
    setJobFinal(null);
    setJobStatus(pastJob.status);
    setJob({
      id: pastJob.id,
      dryRun: pastJob.dryRun,
      totalInvoices: pastJob.totalInvoices,
    });
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/accounting/wsib/jobs/${pastJob.id}/result`,
        { headers: { Authorization: `Bearer ${String(token ?? '').trim()}` } }
      );
      setJobFinal(data);
      if (data?.status) setJobStatus(data.status);
    } catch {
      /* SSE will surface the live state if /result fails */
    }
  }, [token]);

  const resultsRef = useRef(null);
  useEffect(() => {
    if (result && resultsRef.current) resultsRef.current.focus();
  }, [result]);

  const activeStep = job
    ? 3
    : uploading
      ? 1
      : result
        ? 2
        : 0;

  /* ---------- Upload handler ---------- */

  const handleFileSelected = async (selected) => {
    if (!selected) return;
    setFile(selected);
    setUploadError(null);
    setResult(null);
    setJob(null);
    setJobEvents([]);
    setJobStatus(null);
    setJobFinal(null);
    setUploading(true);

    const form = new FormData();
    form.append('file', selected);

    try {
      const { data } = await axios.post(
        `${API_BASE}/api/accounting/wsib/validate`,
        form,
        { headers: { Authorization: `Bearer ${String(token ?? '').trim()}` } }
      );
      setResult(data);
    } catch (e) {
      setUploadError(e?.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setUploadError(null);
    setJob(null);
    setJobEvents([]);
    setJobStatus(null);
    setJobFinal(null);
  };

  /* ---------- Submit flow ---------- */

  const handleContinueToSubmit = () => setCredsOpen(true);

  const handleCredsSubmitted = (creds) => {
    setPendingCreds(creds);
    setCredsOpen(false);
    if (creds.dryRun) {
      void startSubmitJob(creds);
    } else {
      setConfirmOpen(true);
    }
  };

  const handleLiveConfirmed = () => {
    setConfirmOpen(false);
    void startSubmitJob(pendingCreds);
  };

  const handleLiveCancelled = () => {
    setConfirmOpen(false);
    setPendingCreds(null);
  };

  const startSubmitJob = useCallback(async (creds) => {
    const form = new FormData();
    form.append('file', file);
    form.append('telusLogin', creds.telusLogin);
    form.append('telusUsername', creds.telusUsername);
    form.append('telusPassword', creds.telusPassword);
    form.append('dryRun', String(creds.dryRun));

    try {
      const { data } = await axios.post(
        `${API_BASE}/api/accounting/wsib/submit`,
        form,
        { headers: { Authorization: `Bearer ${String(token ?? '').trim()}` } }
      );
      setPendingCreds(null); // drop creds from memory
      setJob(data);
      setJobStatus('running');
      setJobEvents([]);
    } catch (e) {
      setUploadError(e?.response?.data?.error || e.message || 'Submit failed');
      setPendingCreds(null);
    }
  }, [file, token]);

  /* ---------- SSE subscription ---------- */

  useEffect(() => {
    if (!job?.id) return;
    const url = `${API_BASE}/api/accounting/wsib/jobs/${job.id}/progress?token=${encodeURIComponent(String(token || ''))}`;
    const resultUrl = `${API_BASE}/api/accounting/wsib/jobs/${job.id}/result`;
    const headers = { Authorization: `Bearer ${String(token ?? '').trim()}` };
    const es = new EventSource(url);
    let poll = null;
    setStreamLost(false);

    es.onmessage = (ev) => {
      let data;
      // receivedAt is the browser's clock; elapsed-time math must not mix it with the server's t.
      try { data = { ...JSON.parse(ev.data), receivedAt: Date.now() }; } catch { return; }
      setJobEvents(prev => {
        if (data.seq != null && prev.some(e => e.seq === data.seq)) return prev;
        return [...prev, data];
      });
      if (data.type === 'done') {
        setJobStatus(data.status || 'done');
        es.close();
        // Fetch full result + summary.
        axios.get(resultUrl, { headers }).then(r => setJobFinal(r.data)).catch(() => { /* ignore */ });
      }
    };

    es.onerror = () => {
      // EventSource retries transient errors itself. CLOSED means the server
      // refused the stream (restarted, job no longer in memory), so fall back
      // to polling the stored result.
      if (es.readyState !== EventSource.CLOSED || poll) return;
      setStreamLost(true);
      poll = setInterval(async () => {
        try {
          const { data } = await axios.get(resultUrl, { headers });
          if (!data?.status) return;
          setJobFinal(data);
          if (data.status !== 'running' && data.status !== 'pending') {
            clearInterval(poll);
            setJobStatus(data.status);
          }
        } catch { /* keep polling */ }
      }, 10_000);
    };

    return () => {
      es.close();
      if (poll) clearInterval(poll);
    };
  }, [job?.id, token]);

  /* ---------- Render ---------- */

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="lg" className="dashboard-container">
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <img
            src="/images/rct-logo.png"
            alt="Rapid City Transportation Logo"
            style={{ height: 52, width: 'auto' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {user?.email && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {user.email}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={logout}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderColor: 'grey.400',
                color: 'grey.700',
                '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
              }}
            >
              Log Out
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: { xs: 'block', sm: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200', pb: 2.5, pt: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pb: 2,
            }}
          >
            {user?.email && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {user.email}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={logout}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderColor: 'grey.400',
                color: 'grey.700',
                '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
              }}
            >
              Log Out
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src="/images/rct-logo.png"
              alt="Rapid City Transportation Logo"
              style={{ height: 36, width: 'auto' }}
            />
          </Box>
        </Box>

        <Box sx={{ py: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Submit WSIB invoices
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload the &quot;Invoice Detail 1&quot; spreadsheet exported from
              the internal system. The system will validate service codes,
              calculate HST, and (on live submit) drive the Telus Health portal
              on your behalf.
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map(step => (
              <Step key={step.key}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <UploadPanel
            file={file}
            uploading={uploading}
            onFileSelected={handleFileSelected}
            onReset={handleReset}
            hasResult={!!result}
            disabled={!!job && jobStatus === 'running'}
          />

          {uploadError && (
            <Alert severity="error" role="alert" icon={<ErrorIcon />}>
              <AlertTitle>Upload failed</AlertTitle>
              {uploadError}
            </Alert>
          )}

          {result && !job && (
            <Box ref={resultsRef} tabIndex={-1} aria-live="polite" sx={{ outline: 'none' }}>
              <ValidationResults
                result={result}
                onContinue={handleContinueToSubmit}
              />
            </Box>
          )}

          {job && (
            <SubmitProgress
              job={job}
              events={jobEvents}
              status={jobStatus}
              final={jobFinal}
              streamLost={streamLost}
              token={token}
              onStartOver={handleReset}
            />
          )}

          <PastRunsSection
            jobs={pastJobs}
            loading={pastJobsLoading}
            error={pastJobsError}
            token={token}
            onRefresh={loadPastJobs}
            onWatch={handleWatchJob}
            currentJobId={job?.id}
          />
        </Stack>
        </Box>
      </Container>

      <CredentialsDialog
        open={credsOpen}
        summary={result?.summary}
        onSubmit={handleCredsSubmitted}
        onCancel={() => setCredsOpen(false)}
      />
      <LiveConfirmDialog
        open={confirmOpen}
        summary={result?.summary}
        invoices={result?.invoices}
        onConfirm={handleLiveConfirmed}
        onCancel={handleLiveCancelled}
      />
    </Box>
  );
}

/* ---------- Upload + validation sub-components ---------- */

function UploadPanel({ file, uploading, onFileSelected, onReset, hasResult, disabled }) {
  const inputRef = useRef(null);
  const locked = uploading || disabled;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h2" gutterBottom>Spreadsheet</Typography>
          <Typography variant="body2" color="text.secondary">
            Accepted formats: <code>.xlsx</code>, <code>.xls</code>. Maximum size: 10&nbsp;MB.
          </Typography>
          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={locked}
          >
            {file ? 'Choose another file' : 'Choose file'}
          </Button>
          {hasResult && (
            <Button variant="outlined" onClick={onReset} disabled={locked}>
              Start over
            </Button>
          )}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          hidden
          onChange={(e) => { const f = e.target.files?.[0]; onFileSelected(f ?? null); e.target.value = ''; }}
          aria-label="Upload WSIB invoice spreadsheet"
        />
      </Stack>
      {uploading && (
        <Box sx={{ mt: 2 }} aria-live="polite">
          <LinearProgress aria-label="Validating upload" />
          <Typography variant="caption" color="text.secondary">Parsing and validating…</Typography>
        </Box>
      )}
    </Paper>
  );
}

function ValidationResults({ result, onContinue }) {
  const { summary, invoices, missingColumns } = result;
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const hasBlockingErrors = summary.invalidRows > 0;
  const readyForSubmit = summary.invoiceCount > 0 && invoices.some(i => i.valid);

  return (
    <Stack spacing={3}>
      <SummaryCard summary={summary} invoices={invoices} />

      {missingColumns?.length > 0 && (
        <Alert severity="info" icon={<WarningAmberIcon />}>
          <AlertTitle>Optional columns not found</AlertTitle>
          The spreadsheet does not contain a column for: {missingColumns.join(', ')}.
          Service codes will be derived from vehicle type and service type.
        </Alert>
      )}

      {hasBlockingErrors && (
        <Alert severity="error" icon={<ErrorIcon />} role="alert">
          <AlertTitle>
            {summary.invalidRows} {summary.invalidRows === 1 ? 'row has' : 'rows have'} errors
          </AlertTitle>
          Invalid rows will be skipped. Fix them in the source spreadsheet and re-upload,
          or proceed to submit only the valid rows.
        </Alert>
      )}

      <InvoicesGrid invoices={invoices} onSelect={setSelectedInvoice} />

      <Divider />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h2">Continue to Telus Health</Typography>
          <Typography variant="body2" color="text.secondary">
            Next step: choose which Telus login to use, enter your Telus credentials,
            and choose whether to run a dry-run or a live submission.
          </Typography>
        </Box>
        <Button variant="contained" disabled={!readyForSubmit} onClick={onContinue}>
          Continue to submit
        </Button>
      </Stack>

      <InvoiceDetailDialog
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
      />
    </Stack>
  );
}

function SummaryCard({ summary, invoices }) {
  // Invalid invoices already have subtotal/hst/total = 0 from the validator,
  // so summing the whole list gives the total of just-the-valid invoices.
  const totals = useMemo(() => {
    let subtotal = 0;
    let hst = 0;
    let total = 0;
    for (const inv of invoices || []) {
      subtotal += Number(inv.subtotal) || 0;
      hst      += Number(inv.hst)      || 0;
      total    += Number(inv.total)    || 0;
    }
    return { subtotal, hst, total };
  }, [invoices]);

  const items = [
    { label: 'Invoices',   value: summary.invoiceCount, color: 'default' },
    { label: 'Batches',    value: summary.batchCount,   color: 'default' },
    { label: 'Rows',       value: summary.totalRows,    color: 'default' },
    { label: 'Valid rows', value: summary.validRows,    color: 'success', icon: <CheckCircleIcon /> },
    { label: 'Errors',     value: summary.invalidRows,  color: summary.invalidRows ? 'error' : 'default', icon: <ErrorIcon /> },
    { label: 'Warnings',   value: summary.warningCount, color: summary.warningCount ? 'warning' : 'default', icon: <WarningAmberIcon /> },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom>Validation summary</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {items.map(i => (
          <Chip
            key={i.label}
            label={`${i.label}: ${i.value}`}
            color={i.color}
            variant={i.color === 'default' ? 'outlined' : 'filled'}
            icon={i.icon}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        <Chip label={`Subtotal: $${fmt(totals.subtotal)}`} variant="outlined" />
        <Chip label={`HST: $${fmt(totals.hst)}`} variant="outlined" />
        <Chip label={`Grand total: $${fmt(totals.total)}`} color="primary" />
      </Stack>
    </Paper>
  );
}

function InvoicesGrid({ invoices, onSelect }) {
  const [pageSize, setPageSize] = useState(25);
  const rows = useMemo(
    () => invoices
      .map(inv => ({
        id: inv.invoice,
        status: inv.valid ? 'Ready' : 'Has errors',
        valid: inv.valid,
        invoice: inv.invoice,
        claimNumber: inv.claimNumber,
        claimant: inv.claimant,
        rowCount: inv.rowCount,
        validRowCount: inv.validRowCount,
        rowsLabel: inv.validRowCount === inv.rowCount
          ? String(inv.rowCount)
          : `${inv.validRowCount}/${inv.rowCount}`,
        batches: inv.batches.length,
        subtotal: inv.subtotal,
        hst: inv.hst,
        total: inv.total,
        _full: inv,
      }))
      // Problem invoices first so accounting sees them without scrolling,
      // then alphabetical by invoice number within each group.
      .sort((a, b) => {
        if (a.valid !== b.valid) return a.valid ? 1 : -1;
        return a.invoice.localeCompare(b.invoice);
      }),
    [invoices]
  );

  const columns = useMemo(() => [
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          {params.row.valid
            ? <CheckCircleIcon fontSize="small" color="success" aria-hidden="true" />
            : <ErrorIcon fontSize="small" color="error" aria-hidden="true" />}
          <span>{params.value}</span>
        </Stack>
      ),
    },
    { field: 'invoice',     headerName: 'Invoice #',   width: 120 },
    { field: 'claimNumber', headerName: 'Claim #',     width: 140 },
    { field: 'claimant',    headerName: 'Claimant',    width: 200 },
    {
      field: 'rowsLabel',
      headerName: 'Rows',
      width: 100,
      sortComparator: (_a, _b, p1, p2) => p1.api.getRow(p1.id).rowCount - p2.api.getRow(p2.id).rowCount,
    },
    {
      field: 'batches',
      headerName: 'Batches',
      width: 90,
      type: 'number',
      renderCell: (params) => (
        params.value > 1
          ? <Chip size="small" color="warning" label={params.value} />
          : <span>{params.value}</span>
      ),
    },
    {
      field: 'subtotal',
      headerName: 'Subtotal',
      width: 110,
      type: 'number',
      valueFormatter: ({ value }) => `$${fmt(value)}`,
    },
    {
      field: 'hst',
      headerName: 'HST',
      width: 100,
      type: 'number',
      valueFormatter: ({ value }) => `$${fmt(value)}`,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      type: 'number',
      valueFormatter: ({ value }) => `$${fmt(value)}`,
      cellClassName: 'grid-total',
    },
  ], []);

  if (!invoices.length) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        Invoices ({invoices.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Click any row to see the full breakdown. Use the search box to filter by claimant,
        invoice number, or status.
      </Typography>
      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        disableSelectionOnClick
        onRowClick={(params) => onSelect(params.row._full)}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 300 },
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          },
        }}
        sx={{
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .grid-total': { fontWeight: 600 },
        }}
      />
    </Paper>
  );
}

function InvoiceDetailDialog({ invoice, onClose }) {
  const open = !!invoice;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {invoice && <>Invoice {invoice.invoice}</>}
        {invoice && (invoice.valid
          ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Ready" />
          : <Chip size="small" color="error" icon={<ErrorIcon />} label="Has errors" />)}
      </DialogTitle>
      <DialogContent dividers>
        {invoice && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Claim</Typography>
              <Typography>
                {invoice.claimNumber} - {invoice.claimant}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Subtotal: $${fmt(invoice.subtotal)}`} variant="outlined" />
              <Chip label={`HST: $${fmt(invoice.hst)}`} variant="outlined" />
              <Chip label={`Total: $${fmt(invoice.total)}`} color="primary" />
              <Chip
                label={`${invoice.rowCount} row${invoice.rowCount === 1 ? '' : 's'}${
                  invoice.validRowCount !== invoice.rowCount ? ` (${invoice.validRowCount} valid)` : ''
                }`}
                variant="outlined"
              />
              {invoice.batches.length > 1 && (
                <Chip
                  color="warning"
                  label={`${invoice.batches.length} submission batches`}
                />
              )}
            </Stack>

            {invoice.errors.length > 0 && (
              <Alert severity="error" icon={<ErrorIcon />}>
                <AlertTitle>Errors ({invoice.errors.length})</AlertTitle>
                <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.5}>
                  {invoice.errors.map((e, i) => (
                    <Box component="li" key={i}>
                      <Typography variant="body2">{e}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Alert>
            )}

            {invoice.batches.map((batch, idx) => (
              <InvoiceBatchTable
                key={idx}
                batch={batch}
                label={
                  invoice.batches.length > 1
                    ? `Batch ${idx + 1} of ${invoice.batches.length}`
                    : 'Line items'
                }
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function InvoiceBatchTable({ batch, label }) {
  const lines = [...batch.trips, batch.hstLine];
  return (
    <Box>
      <Typography variant="subtitle2" component="h3" gutterBottom>{label}</Typography>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': { borderBottom: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
          '& th': { fontWeight: 600, color: 'text.secondary' },
        }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box component="th">Code</Box>
            <Box component="th">Date</Box>
            <Box component="th">Units</Box>
            <Box component="th" sx={{ textAlign: 'right !important' }}>Charge</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {lines.map((line, j) => (
            <Box
              component="tr"
              key={j}
              sx={{ '& td': { fontWeight: line.lineType === 'HST' ? 600 : 400 } }}
            >
              <Box component="td">{line.serviceCode}</Box>
              <Box component="td">{line.date}</Box>
              <Box component="td">{line.units}</Box>
              <Box component="td" sx={{ textAlign: 'right !important' }}>
                ${fmt(line.charge)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* ---------- Submit sub-components ---------- */

function CredentialsDialog({ open, summary, onSubmit, onCancel }) {
  const [telusLogin, setTelusLogin] = useState('5455');
  const [telusUsername, setTelusUsername] = useState('');
  const [telusPassword, setTelusPassword] = useState('');
  const [dryRun, setDryRun] = useState(true);

  useEffect(() => {
    if (open) {
      setTelusLogin('5455');
      setTelusUsername('');
      setTelusPassword('');
      setDryRun(true);
    }
  }, [open]);

  const canSubmit = telusUsername.trim().length > 0 && telusPassword.length > 0;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Telus Health credentials</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText>
            Your credentials are used once to drive the Telus portal on your behalf
            and are not stored. Submissions on the Telus side will show up as coming
            from your account.
          </DialogContentText>

          <FormControl>
            <FormLabel id="telus-login-label">Telus login</FormLabel>
            <RadioGroup
              row
              aria-labelledby="telus-login-label"
              value={telusLogin}
              onChange={(e) => setTelusLogin(e.target.value)}
            >
              <FormControlLabel value="5455" control={<Radio />} label="5455–5458 (medical)" />
              <FormControlLabel value="530"  control={<Radio />} label="530-WSIB (legacy)" />
            </RadioGroup>
          </FormControl>

          <TextField
            label="Telus username"
            autoComplete="username"
            value={telusUsername}
            onChange={(e) => setTelusUsername(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Telus password"
            type="password"
            autoComplete="current-password"
            value={telusPassword}
            onChange={(e) => setTelusPassword(e.target.value)}
            fullWidth
            required
          />

          <FormControl>
            <FormLabel id="run-mode-label">Mode</FormLabel>
            <RadioGroup
              aria-labelledby="run-mode-label"
              value={dryRun ? 'dry' : 'live'}
              onChange={(e) => setDryRun(e.target.value === 'dry')}
            >
              <FormControlLabel
                value="dry"
                control={<Radio />}
                label={(
                  <Box>
                    <Typography variant="body2">
                      <strong>Dry run:</strong> fill the form and reach the confirmation
                      page, then exit without submitting.
                    </Typography>
                  </Box>
                )}
              />
              <FormControlLabel
                value="live"
                control={<Radio />}
                label={(
                  <Box>
                    <Typography variant="body2">
                      <strong>Live submit:</strong> actually submit {summary?.invoiceCount ?? 0} invoices
                      to the portal. Irreversible.
                    </Typography>
                  </Box>
                )}
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => onSubmit({ telusLogin, telusUsername, telusPassword, dryRun })}
        >
          {dryRun ? 'Start dry run' : 'Continue to confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LiveConfirmDialog({ open, summary, invoices, onConfirm, onCancel }) {
  const validInvoices = (invoices || []).filter(i => i.valid);
  const grandTotal = validInvoices.reduce((s, i) => s + Number(i.total || 0), 0);
  const batchCount = summary?.batchCount ?? validInvoices.length;
  const splitInvoices = batchCount > validInvoices.length;
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => { if (open) setAcknowledged(false); }, [open]);

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Confirm live submission</DialogTitle>
      <DialogContent>
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          This will submit to the Telus Health portal. WSIB submissions
          cannot be undone from this app.
        </Alert>
        <DialogContentText component="div">
          You&apos;re about to submit <strong>{validInvoices.length}</strong> invoices
          {splitInvoices && (
            <> across <strong>{batchCount}</strong> portal submissions
            (some invoices have more than 30 lines and are split)</>
          )}
          {' '}totaling <strong>${fmt(grandTotal)}</strong>.
        </DialogContentText>
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
          }
          label="I have reviewed the dry-run / validation results and want to submit live."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" color="warning" disabled={!acknowledged} onClick={onConfirm}>
          Submit live
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const SLOW_INVOICE_MS = 2 * 60 * 1000;

function SubmitProgress({ job, events, status, final, streamLost, token, onStartOver }) {
  const [stopMode, setStopMode] = useState(null); // null | 'graceful' | 'force'
  // Mirrors stopMode so a failed request can tell whether a later one already moved on.
  const stopModeRef = useRef(null);
  const [stopError, setStopError] = useState(null);
  // `mode` outlives `open` so the text doesn't switch while the dialog fades out.
  const [confirm, setConfirm] = useState({ open: false, mode: 'graceful' });
  const [now, setNow] = useState(() => Date.now());
  const stopNowRef = useRef(null);
  const statusRef = useRef(null);
  const invoiceEvents = events.filter(e => e.type === 'invoice-done');
  const latestLog = [...events].reverse().find(e => e.type === 'log');
  const processed = Math.max(invoiceEvents.length, final?.processedInvoices ?? 0);
  const pct = job.totalInvoices ? Math.round((processed / job.totalInvoices) * 100) : 0;
  const isDone = status === 'done' || status === 'failed' || status === 'cancelled';

  const lastStart = [...events].reverse().find(e => e.type === 'invoice-start');
  const lastDone = [...events].reverse().find(e => e.type === 'invoice-done');
  const inFlightSince = lastStart && (!lastDone || lastStart.index > lastDone.index)
    ? (lastStart.receivedAt ?? lastStart.t)
    : null;
  const inFlightMs = inFlightSince ? Math.max(0, now - inFlightSince) : 0;
  const showSlowHint = !isDone && !streamLost && stopMode !== 'force' && inFlightMs >= SLOW_INVOICE_MS;
  useEffect(() => {
    if (isDone) return undefined;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [isDone]);

  useEffect(() => {
    if (isDone) setConfirm(c => ({ ...c, open: false }));
  }, [isDone]);

  // Requesting a stop disables the button that opened the dialog, so MUI's
  // focus restore has nowhere to land; move focus to the next useful place.
  useEffect(() => {
    if (stopMode === 'graceful') (stopNowRef.current ?? statusRef.current)?.focus();
    if (stopMode === 'force') statusRef.current?.focus();
  }, [stopMode]);

  const statusLabel = !isDone
    ? (stopMode === 'force'
        ? 'Stopping now. Closing the Telus session...'
        : stopMode === 'graceful'
          ? 'Stopping after the current invoice finishes...'
          : 'Processing. Please keep this tab open.')
    : status === 'done'
      ? 'Job complete.'
      : status === 'cancelled'
        ? 'Job stopped.'
        : 'Job failed.';

  const openConfirm = (mode) => setConfirm({ open: true, mode });
  const closeConfirm = () => setConfirm(c => ({ ...c, open: false }));

  const changeStopMode = (mode) => {
    stopModeRef.current = mode;
    setStopMode(mode);
  };

  const requestStop = async (force) => {
    closeConfirm();
    const previous = stopMode;
    const mine = force ? 'force' : 'graceful';
    changeStopMode(mine);
    setStopError(null);
    try {
      await axios.post(
        `${API_BASE}/api/accounting/wsib/jobs/${job.id}/cancel`,
        { force },
        { headers: { Authorization: `Bearer ${String(token ?? '').trim()}` } }
      );
    } catch (err) {
      // 409: already finished; the stream or poll catches up on its own.
      if (err?.response?.status === 409) return;
      if (stopModeRef.current !== mine) return;
      changeStopMode(previous);
      setStopError(err?.response?.data?.error || 'Could not reach the server to stop the job. Try again.');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }} aria-live="polite">
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" component="h2">
            {job.dryRun ? 'Dry run' : 'Live submission'}: job {job.id.slice(0, 8)}
          </Typography>
          <Typography variant="body2" color="text.secondary" ref={statusRef} tabIndex={-1} sx={{ outline: 'none' }}>
            {statusLabel}
          </Typography>
        </Box>

        <Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            aria-label={`${processed} of ${job.totalInvoices} invoices processed`}
          />
          <Typography variant="caption" color="text.secondary">
            {processed} / {job.totalInvoices} invoices
            {latestLog && !isDone && <>: {latestLog.message}</>}
          </Typography>
          {showSlowHint && (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1 }}>
              The current invoice has been running for {Math.floor(inFlightMs / 60_000)} minutes.
              The server restarts the Telus session on its own if it stays stuck,
              or you can use Stop now.
            </Alert>
          )}
          {streamLost && !isDone && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Live updates were lost. Checking the job every 10 seconds instead.
            </Alert>
          )}
          {stopError && (
            <Alert severity="error" role="alert" icon={<ErrorIcon />} sx={{ mt: 1 }}>
              {stopError}
            </Alert>
          )}
        </Box>

        {isDone && final && (
          <JobResults job={final} token={token} onStartOver={onStartOver} />
        )}

        {!isDone && (
          <>
            <LiveEventList events={events} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="warning"
                onClick={() => openConfirm('graceful')}
                disabled={stopMode !== null}
              >
                Stop after current invoice
              </Button>
              <Button
                ref={stopNowRef}
                variant="outlined"
                color="error"
                onClick={() => openConfirm('force')}
                disabled={stopMode === 'force'}
              >
                {stopMode === 'force' ? 'Stopping now...' : 'Stop now'}
              </Button>
            </Stack>
          </>
        )}
      </Stack>

      <Dialog
        open={confirm.open}
        onClose={closeConfirm}
        aria-labelledby="confirm-stop-title"
        aria-describedby="confirm-stop-description"
      >
        <DialogTitle id="confirm-stop-title">
          {confirm.mode === 'force' ? 'Stop this submission now?' : 'Stop after the current invoice?'}
        </DialogTitle>
        <DialogContent>
          <Box id="confirm-stop-description">
            <DialogContentText sx={{ mb: 1.5 }}>
              {confirm.mode === 'force'
                ? 'The Telus session is closed immediately. If a bill was mid-submission it is marked Needs check so you can verify it in Telus before resubmitting.'
                : 'The invoice currently being processed will finish, then no further invoices will be submitted.'}
            </DialogContentText>
            <DialogContentText>
              Invoices already submitted to Telus stay submitted - this does not
              undo them. You'll get a results CSV for everything processed up to
              the stop point.
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} autoFocus>
            Keep running
          </Button>
          <Button
            onClick={() => requestStop(confirm.mode === 'force')}
            color={confirm.mode === 'force' ? 'error' : 'warning'}
            variant="contained"
          >
            {confirm.mode === 'force' ? 'Stop now' : 'Stop after current invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

const LIVE_SUCCESS_STATUSES = new Set(['submitted', 'dry-run-ok']);
const LIVE_RECENT_LIMIT = 10;

/**
 * Live feed during an in-progress job. Two sections: "Issues encountered"
 * (every non-success event, never truncated) and "Recent activity" (last
 * N events regardless of status). Issues stay visible even after many
 * successful submissions push them out of the recent feed.
 */
function LiveEventList({ events }) {
  const invoiceEvents = events.filter(e => e.type === 'invoice-done');
  if (invoiceEvents.length === 0) return null;

  const issues = invoiceEvents.filter(e => !LIVE_SUCCESS_STATUSES.has(e.status));
  const recent = invoiceEvents.slice(-LIVE_RECENT_LIMIT);

  return (
    <Stack spacing={2.5}>
      {issues.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" component="h3" sx={{ m: 0 }}>
              Issues encountered
            </Typography>
            <Chip
              size="small"
              color="warning"
              label={issues.length}
              aria-label={`${issues.length} issue${issues.length === 1 ? '' : 's'} so far`}
            />
          </Stack>
          <Stack
            component="ul"
            spacing={0.5}
            role="list"
            aria-live="polite"
            aria-label="All issues encountered during this run"
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {issues.map((e, i) => (
              <LiveEventRow key={`issue-${e.invoice}-${i}`} event={e} />
            ))}
          </Stack>
        </Box>
      )}

      <Box>
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" component="h3" sx={{ m: 0 }}>
            Recent activity
          </Typography>
          {/* Caption only when truncation is happening. */}
          {invoiceEvents.length > LIVE_RECENT_LIMIT && (
            <Typography
              variant="caption"
              color="text.secondary"
              aria-label={`Showing the ${LIVE_RECENT_LIMIT} most recent of ${invoiceEvents.length} processed invoices; older entries are hidden`}
            >
              (showing {LIVE_RECENT_LIMIT} most recent of {invoiceEvents.length})
            </Typography>
          )}
        </Stack>
        <Stack
          component="ul"
          spacing={0.5}
          role="list"
          aria-live="polite"
          aria-label="Most recent submission activity"
          sx={{ listStyle: 'none', m: 0, p: 0 }}
        >
          {recent.map((e, i) => (
            <LiveEventRow key={`recent-${e.invoice}-${i}`} event={e} />
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

function LiveEventRow({ event }) {
  const meta = STATUS_META[event.status] || { label: event.status, color: 'default' };
  return (
    <Stack
      component="li"
      direction="row"
      spacing={1}
      alignItems="center"
    >
      <Chip
        size="small"
        color={meta.color}
        label={meta.label}
        aria-label={`Status: ${meta.label}`}
      />
      <Typography variant="body2">
        Invoice {event.invoice}
        {event.reason && <>: {event.reason}</>}
      </Typography>
    </Stack>
  );
}

const PAST_RUN_STATUS_META = {
  pending:   { label: 'Pending',   color: 'default' },
  running:   { label: 'Running',   color: 'info'    },
  done:      { label: 'Complete',  color: 'success' },
  failed:    { label: 'Failed',    color: 'error'   },
  cancelled: { label: 'Cancelled', color: 'warning' },
};

/**
 * Audit log: sortable, filterable, paginated DataGrid of the user's recent
 * jobs. Backed by GET /api/accounting/wsib/jobs.
 */
function PastRunsSection({ jobs, loading, error, token, onRefresh, onWatch, currentJobId }) {
  const [pageSize, setPageSize] = useState(25);

  const rows = useMemo(
    () => jobs.map(j => ({
      ...j,
      _createdDate: j.createdAt ? new Date(j.createdAt) : null,
    })),
    [jobs]
  );

  const columns = useMemo(() => [
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      // Use the label so quick-filter "complete" matches status="done".
      valueGetter: ({ row }) => {
        const meta = PAST_RUN_STATUS_META[row.status];
        return meta ? meta.label : row.status;
      },
      renderCell: ({ row, value }) => {
        const meta = PAST_RUN_STATUS_META[row.status] || { label: value, color: 'default' };
        return (
          <Chip
            size="small"
            color={meta.color}
            label={meta.label}
            aria-label={`Status: ${meta.label}`}
          />
        );
      },
    },
    {
      field: 'sourceFilename',
      headerName: 'File',
      flex: 1,
      minWidth: 220,
      valueGetter: ({ row }) => row.sourceFilename || `Job ${row.id.slice(0, 8)}`,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>{params.value}</Typography>
          {params.row.dryRun && (
            <Chip size="small" variant="outlined" label="Dry run" />
          )}
        </Stack>
      ),
    },
    {
      field: '_createdDate',
      headerName: 'Date',
      width: 180,
      type: 'dateTime',
      valueFormatter: ({ value }) => value ? value.toLocaleString() : '-',
    },
    {
      field: 'telusLogin',
      headerName: 'Login',
      width: 90,
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      width: 280,
      sortable: false,
      // Only includes a label when the count is non-zero so "fail" doesn't
      // match every row via a hard-coded "Fail 0".
      valueGetter: ({ row }) => {
        const parts = [];
        if (row.successCount > 0) parts.push(`OK ${row.successCount}`);
        if (row.skipCount > 0) parts.push(`Skip ${row.skipCount}`);
        if (row.unverifiedCount > 0) parts.push(`Check ${row.unverifiedCount}`);
        if (row.failCount > 0) parts.push(`Fail ${row.failCount}`);
        if (row.validationFailedCount > 0) parts.push(`Validation ${row.validationFailedCount}`);
        return parts.join(' ');
      },
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`OK ${row.successCount}`} />
          <Chip size="small" variant="outlined" label={`Skip ${row.skipCount}`} />
          {row.unverifiedCount > 0 && (
            <Chip size="small" variant="outlined" color="warning" label={`Check ${row.unverifiedCount}`} />
          )}
          <Chip size="small" variant="outlined" label={`Fail ${row.failCount}`} />
          {row.validationFailedCount > 0 && (
            <Chip size="small" variant="outlined" color="error" label={`Val ${row.validationFailedCount}`} />
          )}
        </Stack>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const isRunning = row.status === 'pending' || row.status === 'running';
        const isCurrent = currentJobId === row.id;
        if (isRunning) {
          return (
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={() => onWatch?.(row)}
              disabled={isCurrent}
              aria-label={`Watch live progress for ${row.sourceFilename || row.id}`}
            >
              {isCurrent ? 'Watching' : 'Watch'}
            </Button>
          );
        }
        const csvUrl = `${API_BASE}/api/accounting/wsib/jobs/${row.id}/result.csv?token=${encodeURIComponent(String(token || ''))}`;
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            component="a"
            href={csvUrl}
            download={`wsib-results-${row.id}.csv`}
            aria-label={`Download CSV for ${row.sourceFilename || row.id}`}
          >
            CSV
          </Button>
        );
      },
    },
  ], [token, onWatch, currentJobId]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      aria-busy={loading}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h6" component="h2">Past runs</Typography>
          {jobs.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              aria-label={`${jobs.length} past submission${jobs.length === 1 ? '' : 's'} loaded`}
            >
              ({jobs.length})
            </Typography>
          )}
        </Stack>
        <Button size="small" onClick={onRefresh} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} role="alert">
          {error}
        </Alert>
      )}

      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        disableSelectionOnClick
        getRowClassName={(params) => params.id === currentJobId ? 'past-run-current' : ''}
        components={{
          Toolbar: GridToolbar,
          NoRowsOverlay: () => (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No previous submissions yet. After your first run, it'll show up here.
              </Typography>
            </Stack>
          ),
        }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: {
              debounceMs: 100,
              placeholder: 'Search by file, status, or counts',
              sx: { width: 340 },
            },
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          },
        }}
        initialState={{
          sorting: { sortModel: [{ field: '_createdDate', sort: 'desc' }] },
        }}
        sx={{
          // Highlight the currently-watched row.
          '& .past-run-current': {
            bgcolor: 'action.selected',
          },
        }}
        aria-label="Recent submission jobs"
      />
    </Paper>
  );
}

function JobResults({ job, token, onStartOver }) {
  const csvUrl = `${API_BASE}/api/accounting/wsib/jobs/${job.id}/result.csv?token=${encodeURIComponent(String(token || ''))}`;
  const validationFailedCount = job.validationFailedCount ?? 0;
  // Computed from results because we don't persist this counter in SQL -
  // see jobs.js recordBatchResult.
  const unverifiedCount = (job.results || []).filter(r => r.status === 'submit-unverified').length;
  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip
          color="success"
          icon={<CheckCircleIcon />}
          label={`Successful: ${job.successCount}`}
        />
        <Chip
          color={job.skipCount ? 'warning' : 'default'}
          icon={<WarningAmberIcon />}
          variant={job.skipCount ? 'filled' : 'outlined'}
          label={`Skipped: ${job.skipCount}`}
        />
        <Chip
          color={unverifiedCount ? 'warning' : 'default'}
          icon={<WarningAmberIcon />}
          variant={unverifiedCount ? 'filled' : 'outlined'}
          label={`Needs check: ${unverifiedCount}`}
        />
        <Chip
          color={job.failCount ? 'error' : 'default'}
          icon={<ErrorIcon />}
          variant={job.failCount ? 'filled' : 'outlined'}
          label={`Failed: ${job.failCount}`}
        />
        <Chip
          color={validationFailedCount ? 'error' : 'default'}
          icon={<ErrorIcon />}
          variant={validationFailedCount ? 'filled' : 'outlined'}
          label={`Validation failed: ${validationFailedCount}`}
        />
      </Stack>

      <ResultsGrid rows={job.results} />

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          component="a"
          href={csvUrl}
          download={`wsib-results-${job.id}.csv`}
        >
          Download results CSV
        </Button>
        <Button variant="outlined" onClick={onStartOver}>
          Start over
        </Button>
      </Stack>
    </Box>
  );
}

const FAILED_STATUSES = new Set([
  'name-mismatch',
  'claim-mismatch',
  'claim-not-found',
  'validation-failed',
  'submit-unverified',
  'submitted-amount-mismatch',
  'error',
]);

function ResultsGrid({ rows }) {
  const [filter, setFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);

  const failedCount = useMemo(
    () => rows.filter(r => FAILED_STATUSES.has(r.status)).length,
    [rows]
  );

  const gridRows = useMemo(() => {
    const enriched = rows.map((r, i) => ({
      id: `${r.invoice}-${r.batchNumber ?? 1}-${i}`,
      status: r.status,
      invoice: r.invoice,
      claimNumber: r.claimNumber,
      claimant: r.claimant,
      batch: r.batchTotal > 1 ? `${r.batchNumber}/${r.batchTotal}` : '—',
      total: r.total,
      reason: r.reason,
      portalName: r.portalName,
    }));
    return filter === 'failed'
      ? enriched.filter(r => FAILED_STATUSES.has(r.status))
      : enriched;
  }, [rows, filter]);

  const columns = useMemo(() => [
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => {
        const meta = STATUS_META[params.value] || { label: params.value, color: 'default' };
        return <Chip size="small" color={meta.color} label={meta.label} />;
      },
    },
    { field: 'invoice',     headerName: 'Invoice #',  width: 110 },
    { field: 'claimNumber', headerName: 'Claim #',    width: 140 },
    { field: 'claimant',    headerName: 'Claimant',   width: 180 },
    { field: 'batch',       headerName: 'Batch',      width: 80 },
    {
      field: 'total',
      headerName: 'Total',
      width: 110,
      type: 'number',
      valueFormatter: ({ value }) => `$${fmt(value)}`,
    },
    {
      field: 'reason',
      headerName: 'Reason',
      flex: 1,
      minWidth: 240,
      // Wrap long reasons (mismatch messages, confirmation numbers, error
      // text) instead of clipping them with an ellipsis. Row height adjusts
      // automatically below.
      renderCell: ({ value }) => (
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'normal', wordBreak: 'break-word', py: 1 }}
        >
          {value}
        </Typography>
      ),
    },
  ], []);

  if (!rows || rows.length === 0) return null;

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, v) => v && setFilter(v)}
          aria-label="Filter results"
        >
          <ToggleButton value="all">All ({rows.length})</ToggleButton>
          <ToggleButton value="failed" disabled={failedCount === 0}>
            Failed only ({failedCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <DataGrid
        rows={gridRows}
        columns={columns}
        autoHeight
        getRowHeight={() => 'auto'}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        disableSelectionOnClick
        components={{ Toolbar: GridToolbar }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 300 },
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          },
        }}
        sx={{
          // Vertically center cells in auto-height rows so short cells line
          // up with multi-line ones.
          '& .MuiDataGrid-cell': { alignItems: 'center', py: 1 },
        }}
      />
    </Box>
  );
}

/* ---------- Helpers ---------- */

function fmt(n) {
  if (n == null) return '0.00';
  return Number(n).toFixed(2);
}
